import React from "react";
import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import { legacyToActualMap, type Message, type MessageSource } from "../models";
import { ImgDown } from "./Images/ImgDown";
import { ImgUp } from "./Images/ImgUp";
import { ImgFileTxt } from "./Images/ImgFileTxt";
import { ImgWeb } from "./Images/ImgWeb";
import { ImgRetry } from "./Images/ImgRetry";
import { ImgTool } from "./Images/ImgTool.tsx";
import { ThinkingIndicator } from "./ThinkingIndicator";
import { shouldShowModelLoadingProgress } from "./ChatMessageModelLoadingProgress.tsx";
import { ChatMessageSearchProgress } from "./ChatMessageSearchProgress.tsx";
import { ImgCancel } from "./Images/ImgCancel.tsx";
import { ImgDocumentCheck } from "./Images/ImgDocumentCheck";
import { ImgWarning } from "./Images/ImgWarning";
import ContextMenu, { ContextMenuItem } from "./ContextMenu";
import { ImgExternal } from "./Images/ImgExternal.tsx";
import { GitDiff } from "./GitDiff.tsx";
import { MarkdownRenderer } from "./MarkdownRenderer.tsx";

const TOOL_NAME_MAP: Record<string, string> = {
  "get_weather": "Get Weather",
  "get_date_time": "Get Date & Time",
  "fetch_page": "Fetch Page",
  "web_search": "Web Search",
  "telegram_read_channel": "Read Telegram",
  "telegram_read_chat": "Read Telegram",
  "telegram_download_media": "Download Telegram Media",
  "read_youtube": "Read YouTube",
  "audio_to_text": "Transcribe Audio",
  "read_document": "Read Document",
  "generate_sdxl_image": "Generate SDXL Image",
  "generate_flux_image": "Generate Flux Image",
  "generate_z_image_image": "Generate Z-Image",
  "generate_image": "Generate Image",
  "update_image": "Update Image",
  "generate_video": "Generate Video",
  "ocr_image": "OCR Image",
  "generate_docx": "Generate DOCX",
  "generate_xlsx": "Generate XLSX",
  "generate_pptx": "Generate PPTX",
  "web_push": "Send Notification",
  "bitbucket_list_commits": "Bitbucket Commits",
  "bitbucket_list_commits_by_timeframe": "Bitbucket Commits",
  "bitbucket_read_file": "Bitbucket Read File",
  "bitbucket_list_pull_requests": "Bitbucket PRs",
  "bitbucket_get_branch_diff": "Bitbucket Diff",
  "bitbucket_read_commit": "Bitbucket Commit",
  "bitbucket_list_repos": "Bitbucket Repos",
  "bitbucket_me": "Bitbucket Me",
  "jira_me": "Jira Profile",
  "jira_list_projects": "List Projects",
  "jira_search_issues": "Search Issues",
  "jira_get_issue": "Get Issue",
  "jira_list_project_issues": "Project Issues",
  "jira_get_issue_images": "Issue Images",
  "run_agent": "Run Agent",
  "get_skills": "List Skills",
  "run_skill": "Run Skill",
  "create_directory": "Create Directory",
  "rename": "Rename / Move",
  "problems": "Check Syntax",
  "read_file_range": "Read File (Lines)",
  "file_search": "Search Files",
  "text_search": "Search Text",
};

function truncateStr(s: string, max: number): string {
  return s.length > max ? s.substring(0, max) + '…' : s;
}

function shortenUrl(url: string): string {
  try {
    const u = new URL(url);
    const display = u.hostname + (u.pathname !== '/' ? u.pathname : '');
    return truncateStr(display, 50);
  } catch {
    return truncateStr(url, 50);
  }
}

function getToolParamSummary(resolvedName: string, argsJson: string): string | null | React.ReactNode {
  try {
    const a = JSON.parse(argsJson);
    switch (resolvedName) {
      case 'web_search': return a.query ? `"${a.query}"` : null;
      case 'fetch_page': return a.url ? shortenUrl(a.url) : null;
      case 'telegram_read_channel': {
        const parts: string[] = [];
        if (a.channelName) parts.push(`@${a.channelName.replace(/^@/, '')}`);
        if (a.lastHours) parts.push(`${a.lastHours}h`);
        return parts.length ? parts.join(', ') : null;
      }
      case 'telegram_read_chat': {
        const parts: string[] = [];
        if (a.chatName) parts.push(`${a.chatName.replace(/^@/, '')}`);
        if (a.lastHours) parts.push(`${a.lastHours}h`);
        return parts.length ? parts.join(', ') : null;
      }
      case 'telegram_download_media': {
        const parts: string[] = [];
        if (a.chatName) parts.push(`${a.chatName.replace(/^@/, '')}`);
        if (a.messageId) parts.push(`msg ${a.messageId}`);
        return parts.length ? parts.join(', ') : null;
      }
      case 'get_weather': return a.location || null;
      case 'get_date_time': return a.location || null;
      case 'read_youtube': return a.url ? shortenUrl(a.url) : null;
      case 'audio_to_text': return a.url ? shortenUrl(a.url) : null;
      case 'generate_docx':
      case 'generate_xlsx': return a.filename || null;
      case 'generate_pptx': return a.filename || null;
      case 'generate_flux_image':
      case 'generate_z_image_image':
        return a.prompt ? truncateStr(a.prompt, 60) : null;
      case 'generate_sdxl_image':
      case 'generate_image':
        return a.promptPositive ? truncateStr(a.promptPositive, 60) : null;
      case 'update_image': return a.operation || null;
      case 'generate_video': return a.prompt ? truncateStr(a.prompt, 60) : null;
      case 'ocr_image': return a.operation || null;
      case 'web_push': return a.title ? truncateStr(a.title, 40) : null;
      case 'bitbucket_list_commits':
      case 'bitbucket_list_commits_by_timeframe':
        return [a.repo, a.branch].filter(Boolean).join('/') || null;
      case 'bitbucket_read_file':
        return [a.repo, a.path].filter(Boolean).join(': ') || null;
      case 'bitbucket_list_pull_requests':
        return a.repo || null;
      case 'bitbucket_get_branch_diff':
        return [a.repo, a.source].filter(Boolean).join(': ') || null;
      case 'bitbucket_read_commit':
        return [a.repo, a.commitHash?.substring(0, 8)].filter(Boolean).join(': ') || null;
      case 'bitbucket_list_repos':
        return a.workspace || null;
      case 'jira_search_issues': {
        const q = (a.jql as string) || '';
        return q ? truncateStr(q, 60) : null;
      }
      case 'jira_get_issue':
      case 'jira_get_issue_images':
        return a.issueKey || null;
      case 'jira_list_project_issues':
        return a.projectKey || null;
      case 'run_agent': {
        const msg = a.message ? truncateStr(a.message, 60) : null;
        const model = a.model as string | undefined;
        const parts = [msg, model].filter(Boolean);
        return parts.length ? parts.join(', model: ') : null;
      }
      case 'run_skill': {
        const skill = a.skill_name as string | undefined;
        const msg = a.message ? truncateStr(a.message, 60) : null;
        const parts = [skill, msg].filter(Boolean);
        return parts.length ? parts.join(': ') : null;
      }
      case 'list_files':
        return a.path || '/';
      case 'read_file':
        return a.path || null;
      case 'create_file': {
        const path = a.path || null;
        const contentLen = typeof a.content === 'string' ? a.content.length : 0;
        if (!contentLen) return path;
        const lines = (a.content.match(/\n/g) || []).length + 1;
        return (
          <>
            {path}
            <span className="compact-log-param-diff"> (<span className="compact-log-param-diff-added">+{lines}</span>, {contentLen} bytes)</span>
          </>
        );
      }
      case 'edit_file': {
        const filePath = a.path || null;
        const lineDiff = getEditFileLineDiff(argsJson);
        if (!lineDiff) return filePath;
        return (
          <>
            {filePath}
            <span className="compact-log-param-diff"> ({lineDiff})</span>
          </>
        );
      }
      case 'delete_file':
        return a.path || null;
      case 'move_file':
        return [a.sourcePath, a.destinationPath].filter(Boolean).join(' → ') || null;
      case 'create_directory':
        return a.path || null;
      case 'rename':
        return [a.sourcePath, a.destinationPath].filter(Boolean).join(' → ') || null;
      case 'problems': {
        const ext = a.path?.split('.').pop()?.toLowerCase() || '';
        const statusIcon = ext === 'js' ? <ImgDocumentCheck size={10} /> : <ImgWarning size={10} />;
        return a.path ? (<>{statusIcon} {a.path}</>) : null;
      }
      case 'read_file_range': {
        const filePath = a.path || null;
        if (a.startLine != null && a.endLine != null) return `${filePath} [${a.startLine}-${a.endLine}]`;
        if (a.startLine != null) return `${filePath} [${a.startLine}-]`;
        return filePath;
      }
      case 'file_search': {
        const pattern = a.pattern || '';
        const count = a.count as number | undefined;
        return count != null ? `"${pattern}" → ${count} file(s)` : `"${pattern}"`;
      }
      case 'text_search': {
        const pattern = a.pattern || '';
        const count = a.count as number | undefined;
        return count != null ? `"${pattern}" → ${count} line(s)` : `"${pattern}"`;
      }
      default: return null;
    }
  } catch (e) {
    return `Error parsing tool arguments JSON: ${e}`;
  }
}

function getUseBrowser(resolvedName: string, argsJson: string): boolean {
  if (resolvedName !== 'fetch_page') return false;
  try { return JSON.parse(argsJson).useBrowser === true; } catch { return false; }
}

function getFileBrowserLink(chatId: string | null | undefined, resolvedName: string, argsJson: string): string | null {
  // File tools that produce a file path we can link to:
  // create_file, edit_file, read_file → show the file in browser
  // move_file → show destination
  // rename → show destination
  if (!['create_file', 'edit_file', 'read_file', 'move_file', 'rename'].includes(resolvedName)) return null;
  try {
    const a = JSON.parse(argsJson);
    let path: string | undefined;
    switch (resolvedName) {
      case 'create_file':
      case 'edit_file':
      case 'read_file':
        path = a.path;
        break;
      case 'move_file':
        path = a.destinationPath;
        break;
    }
    if (!path) return null;
    // Encode the path for URL safety (same as FilesBrowser)
    const encodedPath = encodeURIComponent(path);
    return `/c/${chatId}/files/${encodedPath}`;
  } catch {
    return null;
  }
}

function getEditFileLineDiff(argsJson: string): React.ReactNode | null {
  // For edit_file, compute accurate +N -M line diff summary using LCS
  try {
    const a = JSON.parse(argsJson);
    const oldText = a.oldText ?? a.old_text;
    const newText = a.newText ?? a.new_text;
    if (!oldText || !newText) return null;
    const oldLines = oldText.split('\n');
    const newLines = newText.split('\n');
    const m = oldLines.length;
    const n = newLines.length;
    // Build LCS table
    const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (oldLines[i - 1] === newLines[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
      }
    }
    const lcsLen = dp[m][n];
    const removed = m - lcsLen;
    const added = n - lcsLen;
    if (removed === 0 && added === 0) return null;
    const parts: React.ReactNode[] = [];
    if (added > 0 || (added === 0 && removed === 0)) {
      parts.push(<span key="a" className="compact-log-param-diff-added">+{added}</span>);
    }
    if (removed > 0 || added === 0) {
      parts.push(<span key="r" className={`compact-log-param-diff-removed${parts.length ? ' with-added' : ''}`}>-{removed}</span>);
    }
    return parts.length ? parts : null;
  } catch { return null; }
}

function formatSize(size: number) {
  return size < 1024
    ? `${size} B`
    : size < 1024 * 1024
      ? `${(size / 1024).toFixed(1)} KB`
      : `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(ns: number) {
  const sec = ns / 1_000_000_000;
  if (sec >= 60) return `${(sec / 60).toFixed(1)}m`;
  if (sec >= 1) return `${sec.toFixed(1)}s`;
  return `${(ns / 1_000_000).toFixed(0)}ms`;
}

type CompactThinkingLogProps = {
  chatId?: string | null;
  message: Message;
  collapsedMessages?: Message[];
  isGenerating: boolean;
  hasModel?: boolean;
  generatingMessageId?: string | null;
  onViewToolResult: (message: Message) => void;
  onRegenerate?: (message: Message) => void;
  executingNode?: string | null;
  previewBlobUrl?: string | null;
  progress?: { current: number; max: number } | null;
  promptId?: string | null;
  onCancel?: () => void;
  showFullThinking?: boolean; // When true: show full log (default expanded); when false: show summary (default collapsed)
  enableFormatting?: boolean; // Follow "Enable code syntax highlighting" setting
};

type StepEntry =
  | { type: 'thinking'; content: string; timeTaken: number | null; message: Message; isAnimating: boolean }
  | { type: 'tool_call'; name: string; resolvedName: string; displayName: string; arguments: string; message: Message; isAnimating: boolean }
  | { type: 'tool_result'; message: Message; size: number; isError: boolean; isAnimating: boolean };

@observer
export class CompactThinkingLog extends React.Component<CompactThinkingLogProps> {
  @observable private _isExpanded: boolean;
  @observable private _bodyVisible: boolean;
  private _collapseTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: CompactThinkingLogProps) {
    super(props);
    this._isExpanded = props.showFullThinking ?? false;
    this._bodyVisible = this._isExpanded;
    makeObservable(this);
  }

  @action private _expand = () => {
    if (this._collapseTimer) { clearTimeout(this._collapseTimer); this._collapseTimer = null; }
    this._bodyVisible = true;
    // One rAF so the body renders at grid 0fr before the open transition fires
    requestAnimationFrame(action(() => { this._isExpanded = true; }));
  };

  @action private _collapse = () => {
    if (!this._isExpanded) return;
    this._isExpanded = false;
    if (this._collapseTimer) clearTimeout(this._collapseTimer);
    this._collapseTimer = setTimeout(action(() => {
      this._bodyVisible = false;
      this._collapseTimer = null;
    }), 300);
  };

  componentWillUnmount(): void {
    if (this._collapseTimer) clearTimeout(this._collapseTimer);
  }

  componentDidUpdate(prevProps: CompactThinkingLogProps): void {
    if (prevProps.showFullThinking !== this.props.showFullThinking) {
      if (this.props.showFullThinking) this._expand();
      else this._collapse();
    }
  }

  render() {
    const { chatId, message, collapsedMessages, isGenerating, hasModel = false, generatingMessageId, onViewToolResult, onRegenerate,
      executingNode, previewBlobUrl, progress, promptId, onCancel, showFullThinking, enableFormatting } = this.props;
    const steps: StepEntry[] = [];

    const messages = collapsedMessages || [ message ];
    // Track tool messages that were already interleaved with a multi-call assistant
    const interleavedToolMsgs = new Set<Message>();

    for (const msg of messages) {
      const isMsgGenerating = isGenerating && msg.id === generatingMessageId;

      if (msg.role === 'assistant') {
        if (msg.thinking) {
          steps.push({
            type: 'thinking',
            content: msg.thinking,
            timeTaken: msg.thinkingTimeTaken ?? null,
            message: msg,
            isAnimating: isMsgGenerating,
          });
        }

        const hasTool = (msg.toolCalls && msg.toolCalls.length > 0) || !!msg.toolsFunctionName;
        if (hasTool && msg.content) {
          steps.push({
            type: 'thinking',
            content: msg.content,
            timeTaken: null,
            message: msg,
            isAnimating: false,
          });
        }

        const toolCalls = msg.toolCalls && msg.toolCalls.length > 0
          ? msg.toolCalls
          : msg.toolsFunctionName
            ? [{ id: 'legacy-0', name: msg.toolsFunctionName, arguments: msg.toolsFunctionArguments || '{}' }]
            : [];

        // For multi-call: collect all subsequent tool messages to interleave
        const toolResultMessages = toolCalls.length > 1
          ? messages.filter(m => m.role === 'tool')
          : null;

        for (let k = 0; k < toolCalls.length; k++) {
          const tc = toolCalls[k];
          const resolvedName = (legacyToActualMap as Record<string, string>)[tc.name] ?? tc.name;
          const displayName = TOOL_NAME_MAP[resolvedName] ?? tc.name;
          steps.push({
            type: 'tool_call',
            name: tc.name,
            resolvedName,
            displayName,
            arguments: tc.arguments || '{}',
            message: msg,
            isAnimating: isMsgGenerating,
          });

          // Interleave matching result immediately after its call
          if (toolResultMessages) {
            const resultMsg = toolResultMessages.find(r => r.toolCallId === tc.id) ?? toolResultMessages[k];
            if (resultMsg) {
              interleavedToolMsgs.add(resultMsg);
              steps.push({
                type: 'tool_result',
                message: resultMsg,
                size: resultMsg.contentLength ?? resultMsg.content?.length ?? 0,
                isError: !!resultMsg.errorMessage,
                isAnimating: isGenerating && resultMsg.id === generatingMessageId,
              });
            }
          }
        }
      }

      // Only add tool_result step for messages that are part of a collapsed sequence.
      // Standalone tool messages (collapsedMessages===undefined) are already displayed
      // by ChatMessageProgress, so we skip them here to avoid duplication.
      if (msg.role === 'tool' && !interleavedToolMsgs.has(msg) && collapsedMessages !== undefined) {
        steps.push({
          type: 'tool_result',
          message: msg,
          size: msg.contentLength ?? msg.content?.length ?? 0,
          isError: !!msg.errorMessage,
          isAnimating: isMsgGenerating,
        });
      }
    }

    const messagesIncluded = collapsedMessages || [message];
    const isThisMessageGenerating = isGenerating && messagesIncluded.some(m => m.id === generatingMessageId);
    // The actual message object that has promptEvalTotal/modelLoadTotal (the inner generating message)
    const generatingMessage = isThisMessageGenerating
      ? (messagesIncluded.find(m => m.id === generatingMessageId) ?? message)
      : message;
    const hasProgressNodes = isThisMessageGenerating && shouldShowModelLoadingProgress(generatingMessage);
    const showWorkingIndicator = isThisMessageGenerating && steps.length === 0 && !hasProgressNodes && !message.content && !message.thinking;

    if (steps.length === 0 && !showWorkingIndicator) return null;

    const hasToolCalls = steps.some(s => s.type === 'tool_call');
    const canExpand = hasToolCalls || steps.length > 1;

    const renderStepProps = { isGenerating, hasModel, onViewToolResult, onRegenerate, executingNode, previewBlobUrl, progress, promptId, onCancel, enableFormatting };

    // Full log content (all steps + progress indicators)
    const renderFullContent = () => (
      <>
        {steps.map((step, i) => (
          <CompactLogStep key={i} chatId={chatId} step={step} {...renderStepProps} />
        ))}
        {showWorkingIndicator && (
          <div className="compact-log-step compact-log-working">
            <div className="compact-log-toggle">
              <CompactLogIcon type="thinking" />
              <ThinkingIndicator><span className="compact-log-label">Working on it</span></ThinkingIndicator>
            </div>
          </div>
        )}
      </>
    );

    if (showFullThinking) {
      return (
        <div className="compact-log">
          {this._bodyVisible && (
            <div className={`compact-log-body${this._isExpanded ? ' compact-log-body--open' : ''}`}>
              <div className="compact-log-body-inner">
                {renderFullContent()}
              </div>
            </div>
          )}
          <button className="compact-log-expand-btn compact-log-expand-btn--collapse" onClick={this._isExpanded ? this._collapse : this._expand}>
            {this._isExpanded
              ? <><ImgUp size={12} /><span>Collapse</span></>
              : <><ImgDown size={12} /><span>Show thinking</span></>
            }
          </button>
        </div>
      );
    } else {
      // showFullThinking=false: summary mode, default collapsed
      const lastToolCall = [...steps].reverse().find(s => s.type === 'tool_call');
      const lastThinking = [...steps].reverse().find(s => s.type === 'thinking');

      const renderSummaryContent = () => {
        if (isThisMessageGenerating) {
          if (lastToolCall) {
            return <CompactLogStep key="summary-tool" chatId={chatId} step={lastToolCall} {...renderStepProps} />;
          }
          if (showWorkingIndicator) {
            return (
              <div className="compact-log-step compact-log-working">
                <div className="compact-log-toggle">
                  <CompactLogIcon type="thinking" />
                  <ThinkingIndicator><span className="compact-log-label">Working on it</span></ThinkingIndicator>
                </div>
              </div>
            );
          }
          return null;
        } else {
          if (lastThinking) {
            return <CompactLogStep key="summary-think" chatId={chatId} step={lastThinking} {...renderStepProps} />;
          }
          return null;
        }
      };

      const summaryContent = renderSummaryContent();
      if (!summaryContent && !canExpand) return null;

      return (
        <div className="compact-log compact-log--summary">
          {!this._isExpanded && summaryContent}
          {this._bodyVisible && (
            <div className={`compact-log-body${this._isExpanded ? ' compact-log-body--open' : ''}`}>
              <div className="compact-log-body-inner">
                {renderFullContent()}
              </div>
            </div>
          )}
          {canExpand && (
            <button className="compact-log-expand-btn" onClick={this._isExpanded ? this._collapse : this._expand}>
              {this._isExpanded
                ? <><ImgUp size={12} /><span>Collapse</span></>
                : <><ImgDown size={12} /><span>Show all</span></>
              }
            </button>
          )}
        </div>
      );
    }
  }
}

type CompactLogStepProps = {
  chatId?: string | null;
  step: StepEntry;
  isGenerating: boolean;
  hasModel: boolean;
  onViewToolResult: (message: Message) => void;
  onRegenerate?: (message: Message) => void;
  executingNode?: string | null;
  previewBlobUrl?: string | null;
  progress?: { current: number; max: number } | null;
  promptId?: string | null;
  onCancel?: () => void;
  enableFormatting?: boolean;
}

@observer
class CompactLogStep extends React.Component<CompactLogStepProps> {
  @observable private _open: boolean;
  @observable private _now: number = Date.now();
  private _intervalId: ReturnType<typeof setInterval> | null = null;
  private _contentRef = React.createRef<HTMLDivElement>();
  private _prevContent: string = '';
  private _prevScrollHeight: number | null = null;
  private _disableAutoScroll: boolean = false;
  private _disableAutoScrollTimeoutId: number | null = null;

  constructor(props: CompactLogStepProps) {
    super(props);
    this._open = props.step.type === 'thinking' && props.step.content.length <= 200;
    if (this._open && props.step.type === 'thinking') {
      this._prevContent = props.step.content;
    }
    makeObservable(this);
  }

  private _onContentScroll = (): void => {
    const contentEl = this._contentRef.current;
    if (!contentEl) return;
    // Only pause auto-scroll when the user scrolls away from the bottom.
    // Programmatic follow-scrolls land at the bottom — and since scroll
    // events fire asynchronously, a flag set-and-reset around the scrollTop
    // assignment can never actually catch them (that was the old stuck bug).
    const distanceFromBottom = contentEl.scrollHeight - contentEl.scrollTop - contentEl.clientHeight;
    if (distanceFromBottom <= 2) return;
    this._disableAutoScroll = true;
    if (this._disableAutoScrollTimeoutId) clearTimeout(this._disableAutoScrollTimeoutId);
    this._disableAutoScrollTimeoutId = window.setTimeout(() => {
      this._disableAutoScroll = false;
      this._disableAutoScrollTimeoutId = null;
    }, 1000);
  };

  private _scrollToEndIfNeeded(): void {
    if (this.props.step.type !== 'thinking') return;

    const contentEl = this._contentRef.current;
    if (!contentEl || !this._open) return;

    // Measure where the user was relative to the bottom BEFORE this content
    // update: scrollTop hasn't changed yet, and the previous frame's height
    // is known. A flat pixel threshold on the post-update height breaks the
    // moment a single streamed chunk (code block, table) grows the content
    // by more than that threshold in one step.
    const prevHeight = this._prevScrollHeight ?? contentEl.scrollHeight;
    const wasNearBottom = prevHeight - contentEl.scrollTop <= contentEl.clientHeight + 40;

    this._prevScrollHeight = contentEl.scrollHeight;
    if (!wasNearBottom || this._disableAutoScroll) return;

    // Defer to rAF so the new content is laid out first, and scroll
    // instantly — an animated scroll leaves the box mid-flight where the
    // next update can't catch up.
    requestAnimationFrame(() => {
      const el = this._contentRef.current;
      if (el && this._open) el.scrollTop = el.scrollHeight;
    });
  }

  public componentDidUpdate(prevProps: CompactLogStepProps): void {
    const currentStep = this.props.step;

    // Scroll to end when content updates during generation
    if (this._open && this.props.isGenerating && currentStep.type === 'thinking' && this._prevContent !== currentStep.content) {
      this._scrollToEndIfNeeded();
    }

    // Update prevContent after scroll attempt
    if (currentStep.type === 'thinking' && this._open) {
      this._prevContent = currentStep.content;
    }

    // Sync interval when animating state changes
    const prevAnimating = prevProps.step.type === 'thinking' && prevProps.step.isAnimating;
    const nowAnimating = currentStep.type === 'thinking' && (currentStep as { isAnimating: boolean }).isAnimating;
    if (prevAnimating !== nowAnimating) this._syncInterval();
  }

  componentDidMount() {
    this._syncInterval();
  }

  public componentWillUnmount(): void {
    if (this._disableAutoScrollTimeoutId) clearTimeout(this._disableAutoScrollTimeoutId);
    if (this._intervalId !== null) { clearInterval(this._intervalId); this._intervalId = null; }
  }

  private _syncInterval() {
    const isThinkingLive = this.props.step.type === 'thinking' && this.props.step.isAnimating;
    if (isThinkingLive) {
      if (this._intervalId === null) {
        this._intervalId = setInterval(action(() => { this._now = Date.now(); }), 100);
      }
    } else {
      if (this._intervalId !== null) { clearInterval(this._intervalId); this._intervalId = null; }
    }
  }

  @action.bound
  private _toggleOpen(): void {
    this._open = !this._open;
    if (this._open && this.props.step.type === 'thinking') {
      this._prevContent = this.props.step.content;
      this._prevScrollHeight = null; // fresh anchor — re-evaluate on first update
      this._disableAutoScroll = false;
    }
  }

  render() {
    const { step, isGenerating, hasModel, onViewToolResult, onRegenerate, chatId, enableFormatting } = this.props;

    if (step.type === 'thinking') {
      let timeLabel = '';
      if (step.timeTaken != null) {
        timeLabel = ` for ${(step.timeTaken / 1000).toFixed(1)}s`;
      } else if (step.isAnimating && step.message._thinkingStartMs !== undefined) {
        const liveSec = (this._now - step.message._thinkingStartMs) / 1000;
        timeLabel = ` for ${liveSec.toFixed(1)}s`;
      }
      return (
        <div className={`compact-log-step compact-log-thinking ${this._open ? 'open' : ''}`}>
          <button className="compact-log-toggle" onClick={this._toggleOpen}>
            <CompactLogIcon type="thinking" />
            <span className="compact-log-label">Thought{timeLabel}</span>
            {this._open ? <ImgUp /> : <ImgDown />}
          </button>
          <div className="compact-log-thinking-content" ref={this._contentRef} onScroll={this._onContentScroll}>
            <MarkdownRenderer
              content={step.content}
              isGenerating={step.isAnimating}
              isThisMessageGenerating={step.isAnimating}
              enableFormatting={enableFormatting ?? false}
            />
          </div>
        </div>
      );
    }

    if (step.type === 'tool_call') {
      const hasArgs = step.arguments && step.arguments !== '{}';
      const paramSummary = getToolParamSummary(step.resolvedName, step.arguments);
      const useBrowser = getUseBrowser(step.resolvedName, step.arguments);
      const fileBrowserLink = getFileBrowserLink(chatId, step.resolvedName, step.arguments);
      const canRegen = !isGenerating && hasModel && !!onRegenerate && !step.isAnimating;
      let formattedArgs: React.ReactNode | null = null;
      
      // For edit_file and create_file, render a git-style diff instead of raw JSON
      if ((step.resolvedName === 'edit_file' || step.resolvedName === 'create_file') && this._open) {
        try {
          const args = JSON.parse(step.arguments);
          const oldText = args.oldText ?? args.old_text;
          const newText = args.newText ?? args.new_text;
          if (step.resolvedName === 'edit_file' && oldText && newText) {
            formattedArgs = <GitDiff oldText={oldText} newText={newText} />;
          } else if (step.resolvedName === 'create_file' && args.content) {
            formattedArgs = <GitDiff oldText="" newText={args.content} />;
          }
        } catch { /* fall through to default rendering */ }
      }
      
      // Default: format as JSON
      if (formattedArgs === null && hasArgs) {
        try { 
          formattedArgs = <pre className="compact-log-args">{JSON.stringify(JSON.parse(step.arguments), null, 2)}</pre>;
        } catch { /* keep as-is - LLM can generate broken JSON */ }
      }
      
      return (
        <div className={`compact-log-step compact-log-tool-call ${this._open ? 'open' : ''}`}>
          <div className="compact-log-row">
            <button className="compact-log-toggle" onClick={() => hasArgs && this._toggleOpen()}>
              <CompactLogIcon type="tool" />
              {step.isAnimating
                ? <ThinkingIndicator><span className="compact-log-label">Executing: {step.displayName}{paramSummary ? <span className="compact-log-param"> — {paramSummary}</span> : null}</span></ThinkingIndicator>
                : <>
                    <span className="compact-log-label">Executed: {step.displayName}{paramSummary ? <span className="compact-log-param"> — {paramSummary}</span> : null}</span>
                    {useBrowser && <ImgWeb size={13} />}
                    {fileBrowserLink && <a href={fileBrowserLink} target="_blank" rel="noopener noreferrer" title="Open file in browser"><ImgExternal size={13} /></a>}
                  </>}
              {hasArgs && (this._open ? <ImgUp /> : <ImgDown />)}
            </button>
            {canRegen && (
              <button className="compact-log-regen-btn" title="Regenerate" onClick={(e) => { e.stopPropagation(); onRegenerate!(step.message); }}>
                <ImgRetry size={12} />
              </button>
            )}
          </div>
          {formattedArgs && (
            <>{formattedArgs}</>
          )}
        </div>
      );
    }

    if (step.type === 'tool_result') {
      const msg = step.message;
      const hasContent = !!msg.content;
      const timeLabel = typeof msg.timeTaken === 'number' ? formatTime(msg.timeTaken) : null;
      const sources = msg.sources;
      const { executingNode, previewBlobUrl, progress, promptId, onCancel } = this.props;
      const hasSearchProgress = !!(msg.toolProgress?.searchResults?.length || msg.toolProgress?.sitesProcessing?.length);
      const statusText = msg.toolProgress?.status;
      const cancelId = msg.toolProgress?.operationId || promptId;
      return (
        <div className={`compact-log-step compact-log-result ${step.isError ? 'compact-log-error' : ''}`}>
          {step.isAnimating && !hasContent && (
            <>
              <div className="compact-log-toggle">
                <CompactLogIcon type="result" />
                {executingNode
                  ? <ThinkingIndicator><span className="compact-log-label">Executing: {executingNode}</span></ThinkingIndicator>
                  : statusText && !hasSearchProgress
                    ? <ThinkingIndicator><span className="compact-log-label">{statusText}</span></ThinkingIndicator>
                    : <ThinkingIndicator><span className="compact-log-label">Receiving result</span></ThinkingIndicator>
                }
                {cancelId && onCancel && (
                  <button className="compact-log-cancel-btn" onClick={onCancel} title="Cancel">
                    <ImgCancel size={12} />
                  </button>
                )}
              </div>
              {hasSearchProgress && (
                <div className="compact-log-search-progress">
                  <ChatMessageSearchProgress message={msg} />
                </div>
              )}
              {previewBlobUrl && (
                <div className="compact-log-image-progress">
                  <img className="compact-log-preview-img" src={previewBlobUrl} alt="Preview" />
                  {progress && (
                    <div className="compact-log-progress-bar">
                      <div className="compact-log-progress-fill" style={{ width: `${Math.min(100, progress.current / progress.max * 100)}%` }}></div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {hasContent && (
            <div className="compact-log-row">
              <div className="compact-log-toggle compact-log-result-row" onClick={() => onViewToolResult(msg)}>
                <ImgFileTxt size={14} />
                <span className="compact-log-label compact-log-result-label">
                  Result{timeLabel ? ` (${timeLabel})` : ''} &mdash; {formatSize(step.size)}
                </span>
                {step.isError && <span className="compact-log-error-badge">error</span>}
              </div>
              {!isGenerating && hasModel && onRegenerate && !step.isAnimating && (
                <button className="compact-log-regen-btn" title="Regenerate" onClick={(e) => { e.stopPropagation(); onRegenerate(msg); }}>
                  <ImgRetry size={12} />
                </button>
              )}
            </div>
          )}
          {sources && sources.length > 0 && (
            <CompactLogSources sources={sources} />
          )}
          {step.isError && msg.errorMessage && (
            <div className="compact-log-error-text">{msg.errorMessage}</div>
          )}
        </div>
      );
    }

    return null;
  }
}

@observer
class CompactLogSources extends React.Component<{ sources: MessageSource[] }> {
  private _buttonRef = React.createRef<HTMLButtonElement>();
  private _maxVisible = 5;

  render() {
    const { sources } = this.props;
    const visibleSources = sources.slice(0, this._maxVisible);
    const extraCount = sources.length - this._maxVisible;

    return (
      <div className="compact-log-sources">
        <button ref={this._buttonRef} className="compact-log-sources-btn" title="View sources">
          <span className="compact-log-sources-favicons">
            {visibleSources.map((s, i) => (
              <span key={i} className="source-favicon-wrap">
                {s.favicon ? (
                  <img
                    src={s.favicon}
                    alt=""
                    className="source-favicon"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.style.display = 'none';
                      (img.nextElementSibling as HTMLElement | null)?.classList.remove('source-favicon-fallback--hidden');
                    }}
                  />
                ) : null}
                <span className={`source-favicon-fallback${s.favicon ? ' source-favicon-fallback--hidden' : ''}`}>
                  {(s.title || s.link).charAt(0).toUpperCase()}
                </span>
              </span>
            ))}
            {extraCount > 0 && <span className="sources-extra">+{extraCount}</span>}
          </span>
          <span className="compact-log-sources-label">{sources.length} source{sources.length !== 1 ? 's' : ''}</span>
        </button>
        <ContextMenu container={this._buttonRef} showOnClick>
          {() => (
            <>
              {sources.map((s, i) => (
                <ContextMenuItem
                  key={i}
                  onClick={() => window.open(s.link, '_blank', 'noopener,noreferrer')}
                  onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); window.open(s.link, '_blank', 'noopener,noreferrer'); } }}
                  title={
                    <span className="source-menu-item">
                      <span className="source-menu-favicon-wrap">
                        {s.favicon ? (
                          <img
                            src={s.favicon}
                            alt=""
                            className="source-menu-favicon"
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.style.display = 'none';
                              (img.nextElementSibling as HTMLElement | null)?.classList.remove('source-favicon-fallback--hidden');
                            }}
                          />
                        ) : null}
                        <span className={`source-favicon-fallback source-menu-favicon-fallback${s.favicon ? ' source-favicon-fallback--hidden' : ''}`}>
                          {(s.title || s.link).charAt(0).toUpperCase()}
                        </span>
                      </span>
                      <span className="source-menu-text">
                        <span className="source-menu-title">{s.title || s.link}</span>
                        <span className="source-menu-url">{s.link}</span>
                      </span>
                      <svg className="source-menu-external" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                        <polyline points="15 3 21 3 21 9"/>
                        <line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                    </span>
                  }
                />
              ))}
            </>
          )}
        </ContextMenu>
      </div>
    );
  }
}

const CompactLogIcon: React.FC<{ type: 'thinking' | 'tool' | 'result' }> = ({ type }) => {
  if (type === 'thinking') {
    return (
      <svg className="compact-log-icon" width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M6 6.5c0-1.1.9-2 2-2s2 .9 2 2c0 .7-.4 1.3-.9 1.6-.3.2-.6.5-.6.9v.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        <circle cx="8" cy="11.5" r=".7" fill="currentColor"/>
      </svg>
    );
  }
  if (type === 'tool') {
    return (
      <ImgTool size={14} />
    );
  }
  return (
    <svg className="compact-log-icon" width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 8L7.5 9.5L10.5 6.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.3"/>
    </svg>
  );
};

export default CompactThinkingLog;